import React, { useEffect, useMemo } from 'react';
import { MapContainer, TileLayer, Marker, useMap, useMapEvents } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

/**
 * BusinessLocationMap — mapa interativo Leaflet para posicionamento do negócio.
 *
 * Comportamentos:
 *  - Sempre visível (mesmo sem coords), começando centralizado no município.
 *  - Auto-refresh: quando `latitude`/`longitude` mudam (via ViaCEP, geocode ou
 *    localização atual), o mapa faz `flyTo` para o novo ponto.
 *  - Clique no mapa: posiciona o marker no clique e chama `onChangeCoords`.
 *  - Marker arrastável: usuário pode ajustar fino após posicionar.
 */

// Fix padrão do Leaflet quando bundler não achou os ícones default
const icon = new L.Icon({
  iconRetinaUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png',
  iconUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png',
  shadowUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png',
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41],
});

// Componente helper para reagir a mudanças de props e centralizar o mapa
function MapController({ center, zoom }) {
  const map = useMap();
  useEffect(() => {
    if (!center) return;
    // flyTo com animação suave
    map.flyTo(center, zoom ?? map.getZoom(), { duration: 0.8 });
  }, [center?.[0], center?.[1], zoom, map]);
  return null;
}

// Handler de clique no mapa — posiciona o marker
function ClickHandler({ onPick }) {
  useMapEvents({
    click(e) {
      onPick({ latitude: e.latlng.lat, longitude: e.latlng.lng });
    },
  });
  return null;
}

export default function BusinessLocationMap({
  latitude,
  longitude,
  municipioCenter, // { lat, lng } opcional — fallback quando sem coords
  onChangeCoords,
  height = 260,
  testId = 'business-location-map',
}) {
  const hasCoords = Number.isFinite(latitude) && Number.isFinite(longitude)
    && latitude !== 0 && longitude !== 0;

  // Centro inicial: coord do negócio > centro do município > Brasil (fallback)
  const initialCenter = useMemo(() => {
    if (hasCoords) return [latitude, longitude];
    if (municipioCenter?.lat && municipioCenter?.lng) return [municipioCenter.lat, municipioCenter.lng];
    return [-14.235, -51.925]; // centro geográfico do Brasil
  }, [hasCoords, latitude, longitude, municipioCenter?.lat, municipioCenter?.lng]);

  const initialZoom = hasCoords ? 17 : municipioCenter ? 13 : 4;

  // Centro reativo — se lat/lng mudam, MapController re-centraliza
  const reactiveCenter = hasCoords
    ? [latitude, longitude]
    : (municipioCenter ? [municipioCenter.lat, municipioCenter.lng] : null);

  return (
    <div className="rounded-xl overflow-hidden border border-gray-200 relative" data-testid={testId}>
      <MapContainer
        center={initialCenter}
        zoom={initialZoom}
        style={{ height: `${height}px`, width: '100%' }}
        scrollWheelZoom={false}
      >
        <TileLayer
          attribution='&copy; <a href="https://openstreetmap.org">OpenStreetMap</a>'
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        />
        {reactiveCenter && <MapController center={reactiveCenter} zoom={hasCoords ? 17 : 13} />}
        <ClickHandler onPick={onChangeCoords} />
        {hasCoords && (
          <Marker
            position={[latitude, longitude]}
            icon={icon}
            draggable
            eventHandlers={{
              dragend: (e) => {
                const pos = e.target.getLatLng();
                onChangeCoords({ latitude: pos.lat, longitude: pos.lng });
              },
            }}
          />
        )}
      </MapContainer>
      {!hasCoords && (
        <div className="absolute top-2 left-2 right-2 z-[400] pointer-events-none">
          <div className="bg-white/95 backdrop-blur border border-amber-200 text-amber-900 rounded-lg px-3 py-2 text-xs font-medium shadow-sm">
            Clique no mapa para posicionar seu negócio ou use &quot;Localização atual&quot;
          </div>
        </div>
      )}
      {hasCoords && (
        <div className="px-4 py-2 bg-gray-50 flex items-center justify-between text-xs">
          <span className="text-gray-500">
            Lat: {latitude.toFixed(6)}, Lng: {longitude.toFixed(6)} · Arraste o marcador para ajustar
          </span>
          <button
            type="button"
            onClick={() => onChangeCoords({ latitude: 0, longitude: 0 })}
            className="text-red-500 hover:underline"
            data-testid="clear-location-btn"
          >
            Limpar
          </button>
        </div>
      )}
    </div>
  );
}
